/* eslint-disable no-console */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { captureException } from '@sentry/react';
import { isFeatureEnabled } from 'common/utils';
import { sanitizeErrorForRum } from 'reportDataSignedUrl';

// Supported error patterns:
// - logger.error(error)                    -> error only (accepts unknown)
// - logger.error('message')                -> message only
// - logger.error('message', error)         -> message + error
// - logger.error('message', context)       -> message + context
// - logger.error(error, context)           -> error + context
// - logger.error('message', context, error) -> message + context + error, whatever was caught: an Error becomes
//   the cause; any other value, such as RTK's `{ status, data }` rejection, is added to the context as `error`

type ErrorArg = string | Error | unknown;
type Context = Record<string, unknown>;

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  return new Error(String(value));
};

const isContext = (value: unknown): value is Context =>
  typeof value === 'object' && value !== null && !(value instanceof Error);

const wrapContext = (ctx: Context | undefined): { ctx: Context } | undefined => (ctx ? { ctx } : undefined);

type ErrorLog = { message: string; err: Error; ctx: Context | undefined };

const readErrorArgs = (messageOrError: ErrorArg, contextOrError?: Context | ErrorArg, error?: ErrorArg): ErrorLog => {
  if (typeof messageOrError !== 'string') {
    // logger.error(error) or logger.error(error, context)
    const err = toError(messageOrError);
    return { message: err.message, err, ctx: isContext(contextOrError) ? contextOrError : undefined };
  }

  const message = messageOrError;
  let err: Error;
  let ctx: Context | undefined;
  if (!isContext(contextOrError) && contextOrError !== undefined) {
    // logger.error('message', error) - wrap with new Error, chain original as cause
    err = new Error(message, { cause: toError(contextOrError) });
  } else {
    // logger.error('message', context) or logger.error('message', context, error)
    // An Error is chained as the cause, keeping its message and stack. Any other value would only read as
    // "[object Object]" there, so it goes in the context, where its fields stay readable.
    ctx = contextOrError;
    if (error instanceof Error) {
      err = new Error(message, { cause: error });
    } else {
      err = new Error(message);
      if (error) ctx = { ...ctx, error };
    }
  }
  // Start the stack at the logger method that called this, as when the Error was built there: Sentry groups
  // issues by their frames, so an extra frame here would re-file every logged error as a new issue.
  (Error as ErrorConstructorWithCaptureStackTrace).captureStackTrace?.(err, readErrorArgs);
  return { message, err, ctx };
};

// Not in the TypeScript lib: Chrome and Edge have it, Firefox from 138, Safari from 17.2.
type ErrorConstructorWithCaptureStackTrace = ErrorConstructor & {
  captureStackTrace?: (target: Error, omitFramesFrom: (...args: never[]) => unknown) => void;
};

const datadogLogger = {
  debug: (message: string, context?: Context) => {
    datadogLogs.logger.debug(message, wrapContext(context));
  },
  error: (messageOrError: ErrorArg, contextOrError?: Context | ErrorArg, error?: ErrorArg) => {
    const { message, err, ctx } = readErrorArgs(messageOrError, contextOrError, error);

    // Log to Datadog (expects: message, context, error)
    datadogLogs.logger.error(message, wrapContext(ctx), err);

    // Also capture in Sentry for error tracking
    if (isFeatureEnabled('SentryEnabled')) {
      captureException(err, { level: 'error', extra: ctx });
    }

    // An error built here has this function as its top frame, which Error Tracking shows as the issue's
    // location and groups by, so RUM gets a copy whose stack starts at the caller. Logs and Sentry keep the
    // original, so their grouping does not change. Where the browser lacks `captureStackTrace`, RUM gets the
    // original too. The caller's own error is never copied.
    const ErrorWithCapture = Error as ErrorConstructorWithCaptureStackTrace;
    let rumError = err;
    if (err !== messageOrError && ErrorWithCapture.captureStackTrace) {
      rumError = new Error(err.message, { cause: err.cause });
      rumError.name = err.name;
      ErrorWithCapture.captureStackTrace(rumError, datadogLogger.error);
    }

    // RUM cannot edit an error's causes before sending, so signed report links and email addresses come
    // out of a copy here. A failure to report is not the caller's failure: leave a trace and carry on.
    try {
      datadogRum.addError(sanitizeErrorForRum(rumError), ctx);
    } catch (reportFailure) {
      // The Logs SDK keeps only an object's enumerable fields, and an Error's message and stack are not.
      datadogLogger.warn('[logger.error] could not report the error to RUM', { error: String(reportFailure) });
    }
  },
  info: (message: string, context?: Context) => {
    datadogLogs.logger.info(message, wrapContext(context));
  },
  warn: (message: string, context?: Context) => {
    datadogLogs.logger.warn(message, wrapContext(context));
  },
};

const formatContext = (ctx?: Context): string => (ctx ? ` ${JSON.stringify(ctx, null, 2)}` : '');

const printError = (messageOrError: ErrorArg, contextOrError?: Context | ErrorArg, error?: ErrorArg) => {
  if (typeof messageOrError !== 'string') {
    const ctx = isContext(contextOrError) ? contextOrError : undefined;
    console.error('%s', toError(messageOrError).message + formatContext(ctx), toError(messageOrError));
  } else if (!isContext(contextOrError) && contextOrError !== undefined) {
    console.error(messageOrError, toError(contextOrError));
  } else if (error && !(error instanceof Error)) {
    console.error(messageOrError + formatContext({ ...contextOrError, error }));
  } else {
    console.error(messageOrError + formatContext(contextOrError), ...(error ? [error] : []));
  }
};

const localLogger = {
  debug: (message: string, context?: Context) => {
    console.debug(message + formatContext(context));
  },
  error: printError,
  info: (message: string, context?: Context) => {
    console.info(message + formatContext(context));
  },
  warn: (message: string, context?: Context) => {
    console.warn(message + formatContext(context));
  },
};

export const logger =
  isFeatureEnabled('DatadogEnabled') && process.env.NODE_ENV !== 'test' ? datadogLogger : localLogger;
